import { KeyboardEvent, ReactNode, useRef } from 'react';
import clsx from 'clsx';

export interface SegmentedOption<T extends string> {
    value: T;
    label: ReactNode;
    /** Small dot marking an option that needs attention (e.g. a language with empty fields). */
    marked?: boolean;
    markedLabel?: string;
    lang?: string;
}

export interface SegmentedProps<T extends string> {
    value: T;
    onChange: (value: T) => void;
    options: SegmentedOption<T>[];
    'aria-label': string;
    className?: string;
}

/** Radio group look-alike for view switches, time ranges and language tabs. Arrow keys, Home and End move. */
export function Segmented<T extends string>({ value, onChange, options, className, ...rest }: SegmentedProps<T>) {
    const refs = useRef<Array<HTMLButtonElement | null>>([]);

    const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
        const last = options.length - 1;
        const target =
            e.key === 'ArrowRight' || e.key === 'ArrowDown'
                ? (index + 1) % options.length
                : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
                  ? (index - 1 + options.length) % options.length
                  : e.key === 'Home'
                    ? 0
                    : e.key === 'End'
                      ? last
                      : null;
        if (target === null) return;
        e.preventDefault();
        onChange(options[target].value);
        refs.current[target]?.focus();
    };

    return (
        <div
            role="radiogroup"
            aria-label={rest['aria-label']}
            className={clsx('inline-flex h-11 max-w-full items-center overflow-x-auto rounded-chip bg-sunken p-1', className)}
        >
            {options.map((o, i) => {
                const selected = o.value === value;
                return (
                    <button
                        key={o.value}
                        ref={(el) => {
                            refs.current[i] = el;
                        }}
                        type="button"
                        role="radio"
                        lang={o.lang}
                        aria-checked={selected}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onChange(o.value)}
                        onKeyDown={(e) => onKeyDown(e, i)}
                        className={clsx(
                            'relative inline-flex h-9 min-w-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-chip px-3 text-small font-medium',
                            'transition-[color,background-color,transform] duration-hover ease-snap active:scale-[0.97] active:duration-press',
                            selected ? 'bg-inverse text-on-inverse' : 'text-muted hover:text-strong'
                        )}
                    >
                        {o.label}
                        {o.marked && (
                            <>
                                <span aria-hidden="true" className="size-1.5 rounded-chip bg-accent" />
                                {o.markedLabel && <span className="sr-only">{o.markedLabel}</span>}
                            </>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
